import React from 'react';
import PropTypes from 'prop-types';
import { get, isNil } from 'lodash';
import styled from 'styled-components';

import { stripUrl, statBandMentions } from '@marketmuse/utilities';

import Tooltip from '../../components/Tooltip/Tooltip';
import Clickable from '../../components/Clickable';
import Rank from '../../components/Rank';
import ContentScore from '../../components/ContentScore';

import MatrixColumn from './MatrixColumn';
import MatrixGrid from './MatrixGrid';
import MatrixColumnLabel from './MatrixColumnLabel';
import MatrixCell from './MatrixCell';
import MatrixLabel from './MatrixLabel';
import MatrixHoverGuide from './MatrixHoverGuide';

const MatrixContainer = styled.div`
  display: flex;
  position: relative;
  * {
    box-sizing: border-box;
  }
`;

const MatrixRank = styled(Rank)``;
const MatrixContentScore = styled(ContentScore)`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 38px;
  border-bottom: 1px solid ${p => p.theme.mmxGreyE9};
  border-top: 1px solid white;
`;

const MatrixLabelQuality = styled(MatrixLabel)`
  border: none;
  margin-bottom: ${p => p.theme.matrixCellSize}px;
  border-bottom: 1px solid ${p => p.theme.mmxGreyE9};
`;

const MatrixCellQuality = styled(MatrixCell)`
  margin-bottom: ${p => p.theme.matrixCellSize}px;
`;

const MatrixColumnGridSingle = styled(MatrixColumn)`
  background-color: white;

  ${p =>
    p.you &&
    `
    margin-left: 0 !important;
    border-left: 0 !important;
  `}
`;

const MatrixColumnGrid = styled(MatrixColumn)`
  position: relative;
  flex-grow: 1;
  min-width: 0 !important;
  margin-top: -320px;
  margin-left: 0 !important;
  border-left: 0 !important;
`;

const MatrixColumnRowLabels = styled(MatrixColumn)`
  background-color: transparent !important;
  width: 280px;
  flex-shrink: 0;
`;

const MatrixColumnHeader = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  height: ${p => p.theme.matrixCellSize}px;
  top: 0;
  left: 0;
  transform: translateX(-100%);
  padding-right: 12px;
  color: ${p => p.theme.mmxDarkGrey};
  font-size: 12px;
`;

const MentionMatrixLabelTop = styled(Clickable)`
  color: white;
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  font-size: 13px;

  img {
    width: 18px;
  }
  &:focus {
    outline: none;
  }
  &:hover {
    & > div {
      cursor: pointer;
      color: transparent;
      &:before {
        content: '';
        background-image: url(data:image/svg+xml;charset=utf-8;base64,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);
        width: 20px;
        height: 16px;
        position: absolute;
        transform: translate(-50%, -50%);
        left: 50%;
        top: 50%;
        background-size: 100%;
        background-repeat: no-repeat;
      }
    }
  }

  ${p =>
    p.you &&
    `
    pointer-events: none;
    cursor: default !important;
  `}
`;

const MentionMatrixLabelTopBadge = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 15px;
  height: 15px;
  min-width: 15px;
  font-size: 6pt;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.theme.mmxDarkGrey};
  color: white;
  margin-left: 3px;
  padding: 3px;
  flex-shrink: 0;
  flex-grow: 0;
`;

const MatrixLabelRow = styled(MatrixLabel)`
  padding-left: 0;
`;

const MatrixCellStyled = styled(MatrixCell)`
  width: ${p => p.theme.matrixCellSize}px;
  height: ${p => p.theme.matrixCellSize}px;
  &:last-child {
    margin-bottom: 0;
  }
`;

class MentionMatrix extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {};

    this.renderLabelRows = this.renderLabelRows.bind(this);
    this.renderLabelRowsLoading = this.renderLabelRowsLoading.bind(this);
    this.renderCells = this.renderCells.bind(this);
    this.renderCellsLoading = this.renderCellsLoading.bind(this);
    this.renderColumn = this.renderColumn.bind(this);
    this.renderColumnLoading = this.renderColumnLoading.bind(this);
    this.renderGrid = this.renderGrid.bind(this);
    this.renderGridLoading = this.renderGridLoading.bind(this);
    this.handleLabelHover = this.handleLabelHover.bind(this);
    this.handleCellHover = this.handleCellHover.bind(this);
    this.renderHoverGuide = this.renderHoverGuide.bind(this);

    this.loadingCellCount = 30;
  }

  renderLabelRowsLoading() {
    return [
      <MatrixLabelQuality
        key="kw-quality"
        loading
        labelHeaderText="Topic"
        labelHeaderPointerText="Content Score"
      />,
    ].concat(
      Array(this.loadingCellCount)
        .fill(null)
        .map((_, i) => (
          <MatrixLabelRow
            key={`kw-${i}`}
            loading
            labelHeaderText={i === 0 ? 'Related Topics' : null}
            labelHeaderPointerText={i === 0 ? 'Distribution' : null}
          />
        )),
    );
  }

  renderLabelRows() {
    const appData = this.props.data;
    const focusTopic = get(appData, 'params.topic');
    const rankingPages = Object.values(
      get(appData, 'scores.ranking_pages') || {},
    );
    const mentions = get(rankingPages, '[0].mentions') || {};

    return [
      <MatrixLabelQuality
        key="kw-quality"
        label={focusTopic}
        labelHeaderText="Topic"
        labelHeaderPointerText="Content Score"
        attrs={{ 'data-mms--matrix': 'grid-legend' }}
      />,
    ].concat(
      Object.keys(mentions).map((topic, i) => (
        <MatrixLabelRow
          key={`kw-${i}`}
          label={topic}
          labelHeaderText={i === 0 ? 'Related Topics' : null}
          labelHeaderPointerText={i === 0 ? 'Distribution' : null}
        />
      )),
    );
  }

  renderCellsLoading() {
    return [<MatrixCellQuality loading key="s-quality" />].concat(
      Array(this.loadingCellCount)
        .fill(null)
        .map((_, i) => <MatrixCellStyled loading key={`s-${i}-m-${i}`} />),
    );
  }

  renderCells(site, index) {
    const appData = this.props.data;
    const contentScoreAverage = get(appData, 'scores.metrics.cs_avg');
    const contentScoreMax = get(appData, 'scores.metrics.cs_max');
    const contentScoreTarget = get(appData, 'scores.metrics.cs_target');
    if (!site || !site.mentions) return null;
    return [
      <MatrixContentScore
        key="matrix-cs"
        value={site.content_score || site.cs}
        average={contentScoreAverage}
        target={contentScoreTarget}
        best={contentScoreMax}
      />,
    ].concat(
      Object.keys(site.mentions).map((mention, i) => {
        const statBand = statBandMentions.calculate(site.mentions[mention]);
        return (
          <MatrixCellStyled
            key={`s-${site.url}-m-${i}`}
            color={statBand.color}
            onMouseEnter={() => this.handleCellHover(mention, true)}
            onMouseLeave={() => this.handleCellHover(mention, false)}
            attrs={{ 'data-mms--matrix': `heat-map--${i + 1}` }}
          />
        );
      }),
    );
  }

  handleLabelHover(id, status) {
    if (status) {
      this.setState({ hover: id });
    } else {
      this.setState({ hover: null });
    }
  }

  handleCellHover(keyword, status) {
    if (status) {
      this.setState({ hoverCell: keyword });
    } else {
      this.setState({ hoverCell: null });
    }
  }

  renderColumnLoading(i) {
    return (
      <MatrixColumnGridSingle key={`s_loading-${i}`}>
        <MatrixColumnLabel loading key={`sl_loading-${i}`} />
        {[...this.renderCellsLoading()]}
      </MatrixColumnGridSingle>
    );
  }

  renderColumn(site, you, index) {
    const appData = this.props.data;
    const youRank = get(
      appData,
      `scores.ranking_pages["${get(site, 'url')}"].rank`,
    );
    const suffix = index ? `-${index}` : '';

    const labelTop = (
      <Tooltip disabled={!!you} title="Compare">
        <MentionMatrixLabelTop
          onClick={() => {
            if (!you) this.props.compareAction(site);
          }}
          you={you}
        >
          {you && !isNil(youRank) && <MatrixRank value={youRank} height={38} />}
          {!you && site && <MatrixRank value={site.rank} height={38} />}
          {site &&
            !isNil(get(appData, `rankingUrlsSerpRankDict['${site.url}']`)) && (
              <MentionMatrixLabelTopBadge>
                {get(appData, `rankingUrlsSerpRankDict['${site.url}']`)}
              </MentionMatrixLabelTopBadge>
            )}
        </MentionMatrixLabelTop>
      </Tooltip>
    );
    return (
      <MatrixColumnGridSingle
        key={`s_col-${site.url}`}
        onMouseEnter={() => this.handleLabelHover(site.url, true)}
        onMouseLeave={() => this.handleLabelHover(site.url, false)}
        you={you}
        attrs={{ 'data-mms--matrix': `grid-column${suffix}` }}
      >
        <MatrixColumnLabel
          key={`sl_col-${site.url}`}
          href={`https://${stripUrl(site.url)}`}
          label={you ? `You ( ${site.url} )` : site.url}
          top={labelTop}
          attrs={{ 'data-mms--matrix': `grid-column-label` }}
        />
        {[...this.renderCells(site, index)]}
      </MatrixColumnGridSingle>
    );
  }

  renderGridLoading() {
    return Array(this.loadingCellCount)
      .fill(null)
      .map((_, i) => this.renderColumnLoading(i));
  }

  renderGrid() {
    const appData = this.props.data;
    const rankingPages = Object.values(
      get(appData, 'scores.ranking_pages') || {},
    ).sort((a, b) => a.rank - b.rank);
    return rankingPages.map((site, index) =>
      this.renderColumn(site, false, index + 1),
    );
  }

  renderHoverGuide(data) {
    const appData = this.props.data;
    const you = Object.values(get(appData, 'scores.pages') || {})[0];
    let site = get(appData, `scores.ranking_pages['${this.state.hover}']`);
    if (!site) {
      site = you || {};
    }
    const hoverKeyword = this.state.hoverCell;
    const siteMentions = site.mentions || {};
    const siteMention = siteMentions[hoverKeyword];

    if (site) {
      return (
        <MatrixHoverGuide
          keyword={hoverKeyword}
          url={site.url}
          mentions={siteMention}
        />
      );
    }
    return null;
  }

  render() {
    const appData = this.props.data;
    const you = Object.values(get(appData, 'scores.pages') || {})[0];
    const error = get(appData, 'scores.error');
    const empty = !get(appData, 'scores');
    const disabled = error || empty;

    return (
      <MatrixContainer>
        <MatrixColumnRowLabels>
          {this.props.loading && this.renderLabelRowsLoading()}
          {!this.props.loading && this.renderLabelRows()}
        </MatrixColumnRowLabels>

        <MatrixColumnGrid>
          <MatrixColumnHeader>
            {this.props.topLabel || 'Rank'}
          </MatrixColumnHeader>
          <MatrixGrid
            loading={this.props.loading}
            locked={() => {
              if (you) return this.renderColumn(you, true, 'you');
              return null;
            }}
          >
            {this.props.loading && this.renderGridLoading()}
            {!this.props.loading && !disabled && this.renderGrid()}
            {!this.props.loading && disabled && this.renderGridLoading()}
          </MatrixGrid>
        </MatrixColumnGrid>

        {this.state.hover && this.state.hoverCell && this.renderHoverGuide()}
      </MatrixContainer>
    );
  }
}

MentionMatrix.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  topLabel: PropTypes.any,
  compareAction: PropTypes.func,
};

export default MentionMatrix;
